// Missions' wire for the shell's <IdentityWall> — ink's lib transcribed
// (parity, not distillation): only the app-name seams differ.
//
// The wall shows nothing that is not a projection of ONE object — the
// relationship between the signed-in identity and Ink — so every function here
// is a read of that relationship and nothing else. Three of the four tabs proxy
// straight to Paradigm through the ink server (see /api/identity/* in
// server.js); the fourth, Personas, rides the transport ink already had.
//
// None of this needs a new grant. `/my-permissions` is introspection, and
// `my-activity` is deliberately exempt from `activity:read` because these are
// the app's OWN receipts.

async function get(path, params) {
  const q = params ? `?${new URLSearchParams(params)}` : '';
  const res = await fetch(`/api/identity/${path}${q}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
  return res.json();
}

// ── Personas ────────────────────────────────────────────────────────────────

/** The roster. Falls back to the server's local roster while my-agents is unmerged. */
export async function loadAgents() {
  try {
    const { agents = [] } = await get('agents');
    return agents;
  } catch {
    // A wall with no agents is a wall with a dead first tab, and the person
    // cannot tell that from "this app offers none". Better to surface nothing
    // and let the picker say so in its own words.
    return [];
  }
}

// ── Library ─────────────────────────────────────────────────────────────────

/**
 * Everything inside the grant. `/nodes` is EP-scoped by construction, so this
 * cannot return a node ink was not granted — the boundary is the platform's.
 *
 * `origin` splits what ink WROTE from what it may merely reach. The gap between
 * those two is the interesting number: it is a picture of unused permission.
 */
export async function loadLibrary({ search, schema, origin, offset, limit }) {
  const { nodes = [], total } = await get('library', {
    limit, offset,
    ...(search ? { search } : {}),
    ...(schema ? { schema_id: schema } : {}),
  });

  // Filtered client-side because `/nodes` has no origin facet — an honest
  // limitation rather than a hidden one: it narrows the PAGE, so the count
  // above the shelf still reports everything the grant exposes.
  const rows = origin === 'all' ? nodes : nodes.filter((n) => {
    const mine = n.created_by_app === 'missions';
    return origin === 'mine' ? mine : !mine;
  });

  return {
    total,
    nodes: rows.map((n) => ({
      id: n.id,
      title: n.title || n.value_json?.title || 'Untitled',
      schema_name: n.schema_name,
      updated_at: n.updated_at ? new Date(n.updated_at).toLocaleDateString() : undefined,
    })),
  };
}

// ── Ledger ──────────────────────────────────────────────────────────────────

/** Every read, write and access ink made — and its agents', each named. */
export async function loadLedger({ page, search, categories }) {
  const data = await get('activity', {
    page, per_page: 50,
    ...(search ? { search } : {}),
    ...(categories ? { categories: categories.join(',') } : {}),
  });
  return {
    total: data.total,
    has_next: data.has_next,
    entries: (data.entries || []).map((e) => {
      // A relationship row's resource_id is the RELATIONSHIP's id, not a
      // node's — treating it as node_id minted an "open" that went nowhere.
      // Its two ENDS ride in details (always exactly two: from → to).
      const isRel = e.category === 'relationship' || /relationship/i.test(e.action || '');
      const d = e.details || {};
      return {
        id: e.id,
        summary: e.summary || e.action,
        // RAW, not preformatted (the wire says `timestamp`; `at` kept first
        // for any older shape): the wall groups rows into Today / Yesterday /
        // … eras itself, and a locale string cannot be bucketed.
        at: e.at || e.timestamp,
        severity: e.severity,
        actor: e.actor,
        schema: e.schema,
        node_id: isRel ? undefined : (e.node_id || e.resource_id),
        title: e.title,
        relationship: isRel && (d.from_node_id || d.to_node_id)
          ? {
              type: d.relationship_type,
              from: { id: d.from_node_id },
              to: { id: d.to_node_id },
            }
          : undefined,
        // Only rows carrying a version can be undone; the wall renders the rest
        // without an undo rather than with a broken one.
        version: e.version,
      };
    }),
  };
}

/** Undo one write. Cuts a NEW version, so undoing is itself reversible. */
export async function revertEntry(entry) {
  const res = await fetch(
    `/api/identity/revert/${entry.node_id}/${entry.version}`,
    { method: 'POST', credentials: 'same-origin' },
  );
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Undo failed');
  return res.json();
}

// ── Exposure ────────────────────────────────────────────────────────────────

/** The live EP ∩ Realm, including the pollable `reauth` signal. */
export async function loadPermissions() {
  try {
    return await get('permissions');
  } catch {
    return null;   // the wall renders "no active authorization"
  }
}

/**
 * The schemas the grant actually exposes, for the Library's facet.
 *
 * Derived from the permissions payload rather than asked for separately: the
 * facet must offer exactly what the person allowed, and a second source could
 * offer a schema the grant does not cover.
 */
export function schemasFrom(permissions) {
  const scope = permissions?.effective_verbs?.nodes ? permissions : null;
  if (!scope) return [];
  const ids = permissions.selected_schema_ids || permissions.schema_ids || [];
  return ids.map((id) => ({ id, name: id }));
}
